import { useEffect, useState } from "react";
import { Settings, Loader2, Sparkles, Truck, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { useGearsVisible } from "../../../lib/gearVisibility";

export function DispoGearButton({ onClick, testid, title }) {
    // Aug-2026 (User-Wunsch): Zahnräder nur für Admin + freigegebene Accounts.
    const gearsOk = useGearsVisible();
    if (!gearsOk) return null;
    return (
        <button type="button" onClick={onClick} title={title}
            className="ml-auto p-1.5 rounded-sm text-gray-400 hover:text-gray-800 hover:bg-gray-100 border border-transparent hover:border-gray-200"
            data-testid={testid}>
            <Settings className="w-4 h-4" />
        </button>
    );
}

const AUTO_RULES = [
    "EC-Karte / Überweisung: Rechnung + E-Mail an den Kunden, sobald die Zahlung vollständig eingegangen ist (Zahlungseingänge, Saldo 0).",
    "Finanzierung: sobald Genehmigung vorliegt, Bank & Konditionen vollständig ausgefüllt sind, eine evtl. Anzahlung verbucht ist und alle Finanzierungs-Uploads gültig hochgeladen sind.",
    "Leasing: wie Finanzierung — Rechnungsempfänger ist die Leasinggesellschaft (Anschrift aus der Partnerliste).",
    "Inzahlungnahme: automatische Gutschrift über den Ankaufbetrag nach ordnungsgemäßer, bestätigter Fahrzeugabgabe.",
    "Werkstattauftrag: fertige Aufträge werden mit abgerechnet — unfertige blocken die Auto-Rechnung, Kundendienst/Monteur erhalten Hinweise bis zur Erledigung.",
    "Der E-Mail-Versand läuft über den Freigabe-Postausgang (E-Mail-Center).",
];

export function AutoInvoiceSettingsDialog({ open, onClose, listingId }) {
    const [enabled, setEnabled] = useState(true);
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/dealer/invoice-auto-settings", { params: listingId ? { listing_id: listingId } : {} })
            .then((r) => { setEnabled(r.data?.enabled !== false); setState(r.data?.listing_state || null); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, listingId]);
    const toggle = async () => {
        const v = !enabled;
        setEnabled(v);
        try {
            await api.patch("/dealer/invoice-auto-settings", { enabled: v });
            toast.success(v ? "Auto-Rechnung aktiviert (alle Standorte)" : "Auto-Rechnung deaktiviert (alle Standorte)");
        } catch { toast.error("Speichern fehlgeschlagen"); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[95vw] max-w-lg" data-testid="auto-invoice-settings-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-500" /> Automatische Rechnung</DialogTitle></DialogHeader>
                {loading ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
                    <div className="space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer font-bold text-sm">
                            <input type="checkbox" checked={enabled} onChange={toggle} className="w-4 h-4 accent-orange-500" data-testid="auto-invoice-toggle" />
                            Auto-Rechnung aktiv (gilt für alle Standorte + Hauptsitz)
                        </label>
                        <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                            {AUTO_RULES.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                        {state ? (
                            <div className={`text-xs rounded-sm border p-2 ${state.status === "done" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="auto-invoice-listing-state">
                                {state.status === "done"
                                    ? <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Automatisch erstellt: Rechnung {state.invoice_number}{state.credit_note ? ` · Gutschrift ${state.credit_note}` : ""}{state.emailed ? " · E-Mail im Postausgang" : ""}</span>
                                    : <span className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Status dieses Fahrzeugs: {state.reason || "wartet auf Bedingungen"}</span>}
                            </div>
                        ) : listingId ? (
                            <div className="text-xs rounded-sm border p-2 bg-gray-50 border-gray-200 text-gray-500" data-testid="auto-invoice-listing-state">
                                Für dieses Fahrzeug liegt noch keine automatische Prüfung vor — die Engine prüft alle 10 Minuten verkaufte/reservierte Fahrzeuge ohne Rechnung.
                            </div>
                        ) : null}
                    </div>
                )}
                <DialogFooter><Button variant="outline" onClick={onClose} data-testid="auto-invoice-settings-close">Schließen</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const SHIP_RULES = [
    "EC-Karte / Überweisung: Fahrzeugpapiere + Rechnungsausdruck automatisch per Einschreiben an den Kunden.",
    "Finanzierung: COC + Rechnung an den Kunden — ZB2 (Fahrzeugbrief) mit separatem Anschreiben per Einschreiben an die finanzierende Bank.",
    "Leasing: wie Finanzierung — ZB2 an die Leasinggesellschaft.",
    "Nach der (Auto-)Rechnung werden die Versandaufträge automatisch angelegt.",
];

export function ShippingSettingsDialog({ open, onClose, listingId }) {
    const [st, setSt] = useState(null);
    const [orders, setOrders] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!open) return;
        api.get("/dealer/shipping-settings").then((r) => setSt(r.data || {})).catch(() => setSt({}));
        api.get("/dealer/shipping-orders", { params: listingId ? { listing_id: listingId } : {} })
            .then((r) => setOrders(r.data?.orders || [])).catch(() => {});
    }, [open, listingId]);
    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/dealer/shipping-settings", st);
            toast.success("Versand-Einstellungen gespeichert — gelten für alle Standorte");
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    const f = (k, v) => setSt((p) => ({ ...p, [k]: v }));
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="shipping-settings-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-orange-500" /> Versand-Regeln &amp; Postversand-Zugang</DialogTitle></DialogHeader>
                {!st ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
                    <div className="space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer font-bold text-sm">
                            <input type="checkbox" checked={st.enabled !== false} onChange={(e) => f("enabled", e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="shipping-auto-toggle" />
                            Automatische Versandaufträge nach Rechnungserstellung
                        </label>
                        <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                            {SHIP_RULES.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                        <div className="border-t pt-3">
                            <div className="text-xs font-bold uppercase tracking-wider mb-1.5">Deutsche Post Internetmarke — Zugangsdaten (gelten für alle Standorte)</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div><Label className="text-xs">Portokasse E-Mail</Label><Input value={st.portokasse_email || ""} onChange={(e) => f("portokasse_email", e.target.value)} data-testid="shipping-portokasse-email" /></div>
                                <div><Label className="text-xs">Portokasse Passwort</Label><Input type="password" value={st.portokasse_password || ""} onChange={(e) => f("portokasse_password", e.target.value)} data-testid="shipping-portokasse-password" /></div>
                                <div><Label className="text-xs">API Key (DHL Developer Portal)</Label><Input value={st.app_key || ""} onChange={(e) => f("app_key", e.target.value)} data-testid="shipping-app-key" /></div>
                                <div><Label className="text-xs">API Secret</Label><Input type="password" value={st.app_secret || ""} onChange={(e) => f("app_secret", e.target.value)} data-testid="shipping-app-secret" /></div>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1">Ohne vollständige Zugangsdaten werden Test-Labels (nicht frankaturgültig) erzeugt. API Key/Secret: developer.dhl.com → App mit „Post &amp; DHL Internetmarke" anlegen.</p>
                        </div>
                        {orders.length > 0 && (
                            <div className="border-t pt-3" data-testid="shipping-orders-list">
                                <div className="text-xs font-bold uppercase tracking-wider mb-1.5">Versandaufträge {listingId ? "zu diesem Fahrzeug" : ""}</div>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                    {orders.map((o) => (
                                        <div key={o.id} className="text-xs border border-gray-200 rounded-sm px-2 py-1.5 flex items-center gap-2">
                                            <span className="font-bold shrink-0">{o.recipient_type === "bank" ? "An Bank/Leasing" : "An Kunde"}</span>
                                            <span className="text-gray-500 truncate">{(o.contents || []).join(" + ")} → {o.address?.name}, {o.address?.city}</span>
                                            <span className={`ml-auto shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${o.status === "versendet" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="shipping-settings-cancel">Abbrechen</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#111827] text-white hover:bg-black" data-testid="shipping-settings-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
